import {DestroyRef, inject, Injectable} from '@angular/core';
import {OauthPkceService} from './oauth-pkce.service';
import {RefreshTokenService} from './refresh-token.service';
import {Store} from '@ngxs/store';
import {Router} from '@angular/router';
import {WINDOW_TOKEN} from '../core/tokens/window.token';
import {
  BehaviorSubject,
  catchError,
  combineLatest, distinctUntilChanged,
  EMPTY,
  filter,
  map,
  merge, Observable, of,
  ReplaySubject,
  shareReplay,
  skip, startWith,
  Subject,
  switchMap,
  tap, timeout, withLatestFrom
} from 'rxjs';
import {OAuthConfig} from './oauth-config';
import { AuthState } from "./+state/state";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {Login} from './+state/actions';
import type { AuthError } from './errors';
import { InitializationError } from './errors';

export interface OAuthFlowState {
  isAuthenticated: boolean;
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: number | null;
  isRefreshing: boolean;
  isProcessing: boolean;
  error: AuthError | null;
  stage: 'idle' | 'initiating' | 'callback' | 'authenticated' | 'error';
}

export const DEFAULT_OAUTH_FLOW_STATE: OAuthFlowState = {
  isAuthenticated: false,
  accessToken: null,
  refreshToken: null,
  expiresAt: null,
  isRefreshing: false,
  isProcessing: false,
  error: null,
  stage: 'idle'
};

@Injectable({
  providedIn: 'root'
})
export class OauthFlowService {
  private readonly oauthService = inject(OauthPkceService);
  private readonly refreshTokenService = inject(RefreshTokenService);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly window = inject(WINDOW_TOKEN);
  private readonly destroyRef = inject(DestroyRef);

  // Configuration stream
  private readonly config$ = new ReplaySubject<OAuthConfig>(1);

  // Flow control subjects
  private readonly initiateAuth$ = new Subject<void>();
  private readonly handleCallback$ = new Subject<void>();
  private readonly logout$ = new Subject<void>();
  private readonly error$ = new BehaviorSubject<AuthError | null>(null);
  private readonly stage$ = new BehaviorSubject<OAuthFlowState['stage']>('idle');

  // Store selectors as streams
  private readonly authState$ = combineLatest([
    this.store.select(AuthState.isAuthenticated),
    this.store.select(AuthState.accessToken),
    this.store.select(AuthState.refreshToken),
    this.store.select(AuthState.expiresAt),
    this.store.select(AuthState.isRefreshing)
  ]).pipe(
    map(([isAuthenticated, accessToken, refreshToken, expiresAt, isRefreshing]) => ({
      isAuthenticated,
      accessToken,
      refreshToken,
      expiresAt,
      isRefreshing
    })),
    shareReplay(1)
  );

  // Processing state
  private readonly isProcessing$ = merge(
    this.initiateAuth$.pipe(map(() => true)),
    this.handleCallback$.pipe(map(() => true)),
    this.stage$.pipe(
      map(stage => stage === 'initiating' || stage === 'callback')
    ),
    this.error$.pipe(filter(Boolean), map(() => false))
  ).pipe(
    startWith(false),
    distinctUntilChanged(),
    shareReplay(1)
  );

  // Combined flow state
  readonly state$: Observable<OAuthFlowState> = combineLatest([
    this.authState$,
    this.isProcessing$,
    this.error$,
    this.stage$
  ]).pipe(
    map(([authState, isProcessing, error, stage]) => ({
      ...authState,
      isProcessing,
      error,
      stage
    })),
    shareReplay(1)
  );

  // Handle authentication initiation
  private readonly authInitiationFlow$ = this.initiateAuth$.pipe(
    withLatestFrom(this.config$),
    tap(() => {
      this.error$.next(null);
      this.stage$.next('initiating');
      this.store.dispatch(new Login());
    }),
    switchMap(([, config]) => {
      return of(this.oauthService.initiateAuthentication(config)).pipe(
        catchError((err) => {
          const initError = new InitializationError('Failed to start the authentication process');
          (initError as any).cause = err;
          this.error$.next(initError);
          this.stage$.next('error');
          return EMPTY;
        })
      );
    })
  );

  // Handle OAuth callback
  private readonly callbackFlow$ = this.handleCallback$.pipe(
    withLatestFrom(this.config$),
    tap(() => {
      this.stage$.next('callback');
    }),
    switchMap(([, config]) => {
      return this.oauthService.handleCallback(config).pipe(
        timeout(30000), // 30 second timeout
        tap(() => {
          this.stage$.next('authenticated');
          // Check for stored redirect URL
          const redirectUrl = this.window.sessionStorage.getItem('redirectUrl');
          if (redirectUrl) {
            this.window.sessionStorage.removeItem('redirectUrl');
            this.router.navigateByUrl(redirectUrl);
          }
        }),
        catchError((error: AuthError | any) => {
          let authError: AuthError;
          if (error.name === 'TimeoutError') {
            authError = new InitializationError('Authentication timeout - please try again');
          } else {
            authError = error instanceof Error ? error as AuthError : new InitializationError('Authentication failed');
          }
          this.error$.next(authError);
          this.stage$.next('error');
          return EMPTY;
        })
      );
    })
  );

  // Handle logout
  private readonly logoutFlow$ = this.logout$.pipe(
    withLatestFrom(this.config$),
    tap(([, config]) => {
      this.oauthService.logout(config);
      this.error$.next(null);
      this.stage$.next('idle');
    })
  );

  constructor() {
    // Subscribe to all flows
    merge(
      this.authInitiationFlow$,
      this.callbackFlow$,
      this.logoutFlow$
    ).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();

    // Update stage based on auth state changes
    this.authState$.pipe(
      skip(1), // Skip initial emission
      tap(state => {
        if (state.isAuthenticated && this.stage$.value !== 'authenticated') {
          this.stage$.next('authenticated');
        } else if (!state.isAuthenticated && this.stage$.value === 'authenticated') {
          this.stage$.next('idle');
        }
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  /**
   * Configure OAuth settings
   */
  configure(config: OAuthConfig): void {
    this.config$.next(config);
    this.refreshTokenService.initiate(config);
  }

  /**
   * Initiate OAuth authentication flow
   */
  initiateAuthentication(): void {
    this.initiateAuth$.next();
  }

  /**
   * Handle OAuth callback
   */
  processCallback(): void {
    this.handleCallback$.next();
  }

  /**
   * Logout user
   */
  logout(): void {
    this.logout$.next();
  }


  /**
   * Manually refresh token
   */
  refreshToken(): void {
    this.refreshTokenService.refreshNow();
  }

}
